/**
 * Safe-area mapping for virtual-background assets ().
 *
 * WHY THIS EXISTS. `@livekit/track-processors` maps the background image with
 * `resizeImageToCover(image, canvas.width, canvas.height)` — a CENTER CROP to
 * the camera frame's aspect, then a resize. An asset whose aspect differs from
 * the capture's loses its edges: on a 4:3 camera the 16:9 brand wall has its
 * left and right cut off, and the marketing logo on that wall goes with them —
 * cropped in the teacher's preview AND in the recording, because the crop is
 * baked into the published frame.
 *
 * THE FIX IS PRE-PROCESSING, not a new pipeline: pad (letterbox) the asset to
 * the camera's exact aspect with the brand wall colour BEFORE handing it to
 * the processor. The processor's cover-crop then lands on an image that
 * already matches the frame — the crop becomes a no-op and the whole asset,
 * logo included, is CONTAINED rather than covered. Design should eventually
 * supply a safe-area-native asset (9:16, 4:3 and 16:9 exports or an SVG);
 * until then this keeps every existing build correct.
 */

/** The brand wall's own edge tone, sampled from brand.jpg — pads read as wall. */
export const BRAND_PAD_COLOR = '#1F2A5A'

/**
 * Where an asset of `assetW × assetH` lands inside a `frameW × frameH` frame,
 * contained and centred: the largest uniform scale that fits, with the
 * remaining space split evenly. This is the mapping the pad draws — the pure
 * half of this module, tested in backgroundAsset.test.ts.
 */
export function containPlacement(
  assetW: number,
  assetH: number,
  frameW: number,
  frameH: number,
): { x: number; y: number; width: number; height: number } {
  const scale = Math.min(frameW / assetW, frameH / assetH)
  const width = assetW * scale
  const height = assetH * scale
  return { x: (frameW - width) / 2, y: (frameH - height) / 2, width, height }
}

/** Pad budget below which we do not bother: within 0.5% of the frame is identity. */
const PAD_EPSILON = 0.995

const cache = new Map<string, string>()

/**
 * An `imagePath` the processor can cover-crop without losing any of the
 * asset: the image letterboxed to `frameW × frameH` with `fill` showing in
 * the bars, as a blob URL. Returns null when no padding is needed (the
 * aspects already match) or the browser cannot do it — callers fall back to
 * the original path, which is today's behaviour, not an error.
 *
 * Results are cached per path+frame: the apply effect re-runs on every mode
 * switch, and re-encoding a ~40 KB JPEG on each tap of the picker is exactly
 * the kind of surprise CPU cost this app exists to avoid. The URLs are
 * handful-of-aspects bounded and live for the page; ponytail: revoke in an
 * LRU if the key set ever grows past "a few".
 */
export async function safeAreaBackgroundUrl(
  imagePath: string,
  frameW: number,
  frameH: number,
  fill: string,
): Promise<string | null> {
  if (!Number.isFinite(frameW) || !Number.isFinite(frameH) || frameW <= 0 || frameH <= 0) {
    return null
  }
  const key = `${imagePath}@${Math.round(frameW)}x${Math.round(frameH)}`
  const hit = cache.get(key)
  if (hit) return hit

  const image = await loadImage(imagePath).catch(() => null)
  if (!image) return null
  const place = containPlacement(image.naturalWidth, image.naturalHeight, frameW, frameH)
  if (place.width * place.height >= frameW * frameH * PAD_EPSILON) return null

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(frameW)
  canvas.height = Math.round(frameH)
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = fill
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(image, place.x, place.y, place.width, place.height)
  const url = await new Promise<string | null>((resolve) =>
    canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : null), 'image/jpeg', 0.85),
  )
  if (url) cache.set(key, url)
  return url
}

function loadImage(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`background asset failed to load: ${path}`))
    image.src = path
  })
}
